import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import moment from "moment";
import ImportExportService from "services/ImportExportService";
import Utils from "utils";
import openNotification from "utils/notification";

export const exportMaterialsExcel = createAsyncThunk(
  "material/exportExcel",
  async (params, { rejectWithValue }) => {
    try {
      const response = await ImportExportService.exportMaterialsExcel(params);
      const blob = response?.data || response;
      const url = window.URL.createObjectURL(blob);
      const filename = params?.isMaterialCate ? `Danh sách nguyên vật liệu.xlsx` : `Danh sách công cụ dụng cụ.xlsx`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error"
      );
    }
  }
);

export const importMaterialsGoogleSheets = createAsyncThunk(
  "material/importGoogleSheets",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await ImportExportService.importMaterialsGoogleSheets(
        payload
      );

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title")
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error"
      );
    }
  }
);

export const exportProductsExcel = createAsyncThunk(
  "product/exportExcel",
  async (params, { rejectWithValue }) => {
    try {
      const response = await ImportExportService.exportProductsExcel(params);
      const blob = response?.data || response;
      const url = window.URL.createObjectURL(blob);
      const filename = `Danh sách sản phẩm.xlsx`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error"
      );
    }
  }
);

export const importProductsGoogleSheets = createAsyncThunk(
  "product/importGoogleSheets",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await ImportExportService.importProductsGoogleSheets(
        payload
      );

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title")
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error"
      );
    }
  }
);

export const exportProcessingProducts = createAsyncThunk(
  "product/exportProcessingProducts",
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await ImportExportService.exportProcessingProducts(params);
      const blob = response?.data || response;
      const url = window.URL.createObjectURL(blob);
      const filename = `Danh sách thành phẩm_${moment().format("YYYYMMDD")}.xlsx`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error",
      );
    }
  },
);

export const exportInventoryProducts = createAsyncThunk(
  "product/exportInventoryProducts",
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await ImportExportService.exportInventoryProducts(params);
      const blob = response?.data || response;
      const url = window.URL.createObjectURL(blob);
      const filename = `Danh sách hàng hóa_${moment().format("YYYYMMDD")}.xlsx`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error",
      );
    }
  },
);

export const exportWarehouseCheckExcel = createAsyncThunk(
  "warehouseCheck/exportExcel",
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await ImportExportService.exportWarehouseCheckExcel(params);
      const blob = response?.data || response;
      const url = window.URL.createObjectURL(blob);
      const filename = `Phiếu kiểm kho_${moment().format("YYYYMMDD")}.xlsx`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error",
      );
    }
  },
);

export const importWarehouseCheckGoogleSheets = createAsyncThunk(
  "warehouseCheck/importGoogleSheets",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response =
        await ImportExportService.importWarehouseCheckGoogleSheets(payload);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title")
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error"
      );
    }
  }
);

export const importWarehouseCheckExcel = createAsyncThunk(
  "warehouseCheck/importExcel",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, storeId, warehouseCheckId, file } = data;

      const formData = new FormData();
      formData.append("StoreId", storeId);
      formData.append("WarehouseCheckId", warehouseCheckId);
      formData.append("File", file?.originFileObj || file);

      const response =
        await ImportExportService.importWarehouseCheckExcel(formData);

      const result = response?.data || {};

      if (onSuccess) onSuccess(response);

      if (result?.totalCustomerImportFail > 0) {
        openNotification(
          "warning",
          `Cập nhật thành công ${result?.totalCustomerImportSuccess || 0} mặt hàng, lỗi ${result.totalCustomerImportFail} dòng`,
          Utils.setLocale("admin.settings.popup.notification.title")
        );
      } else {
        openNotification(
          "success",
          Utils.setLocale("admin.settings.popup.notification.success"),
          Utils.setLocale("admin.settings.popup.notification.title")
        );
      }

      return result;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error"
      );
    }
  }
);

export const importEmployeesGoogleSheets = createAsyncThunk(
  "employee/importGoogleSheets",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await ImportExportService.importEmployeesGoogleSheets(
        payload
      );

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title")
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error"
      );
    }
  }
);

export const exportEmployeesExcel = createAsyncThunk(
  "employee/exportExcel",
  async (params, { rejectWithValue }) => {
    try {
      const response = await ImportExportService.exportEmployeesExcel(params);
      const blob = response?.data || response;
      const url = window.URL.createObjectURL(blob);
      const filename = `Danh sách nhân sự.xlsx`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error"
      );
    }
  }
);

export const exportUnitExcel = createAsyncThunk(
  "unit/exportExcel",
  async (params, { rejectWithValue }) => {
    try {
      const response = await ImportExportService.exportUnitExcel(params);
      const blob = response?.data || response;
      const url = window.URL.createObjectURL(blob);
      const filename = `Danh sách đơn vị tính_${moment().format("YYYYMMDD")}.xlsx`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error",
      );
    }
  },
);

export const exportStockDetailExcel = createAsyncThunk(
  "stockDetail/exportExcel",
  async (params, { rejectWithValue }) => {
    try {
      const response = await ImportExportService.exportStockDetailExcel(params);
      const blob = response?.data || response;
      const url = window.URL.createObjectURL(blob);
      const filename = `Chi tiết tồn kho_${moment().format("YYYYMMDD")}.xlsx`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Error",
      );
    }
  },
);

const initialState = {
  loadingImport: false,
  loadingExport: false,
  loadingImportProduct: false,
  loadingExportProduct: false,
  loadingImportWarehouseCheck: false,
  loadingImportWarehouseCheckExcel: false,
  loadingExportWarehouseCheck: false,
  loadingExportUnit: false,
  loadingExportStockDetail: false,
};

export const materialSlice = createSlice({
  name: "material",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // IMPORT MATERIAL
      .addCase(importMaterialsGoogleSheets.pending, (state) => {
        state.loadingImport = true;
      })
      .addCase(importMaterialsGoogleSheets.fulfilled, (state) => {
        state.loadingImport = false;
      })
      .addCase(importMaterialsGoogleSheets.rejected, (state) => {
        state.loadingImport = false;
      })

      // EXPORT MATERIAL
      .addCase(exportMaterialsExcel.pending, (state) => {
        state.loadingExport = true;
      })
      .addCase(exportMaterialsExcel.fulfilled, (state) => {
        state.loadingExport = false;
      })
      .addCase(exportMaterialsExcel.rejected, (state) => {
        state.loadingExport = false;
      })

      // IMPORT PRODUCT
      .addCase(importProductsGoogleSheets.pending, (state) => {
        state.loadingImportProduct = true;
      })
      .addCase(importProductsGoogleSheets.fulfilled, (state) => {
        state.loadingImportProduct = false;
      })
      .addCase(importProductsGoogleSheets.rejected, (state) => {
        state.loadingImportProduct = false;
      })

      // EXPORT PRODUCT
      .addCase(exportProductsExcel.pending, (state) => {
        state.loadingExportProduct = true;
      })
      .addCase(exportProductsExcel.fulfilled, (state) => {
        state.loadingExportProduct = false;
      })
      .addCase(exportProductsExcel.rejected, (state) => {
        state.loadingExportProduct = false;
      })

      .addCase(exportProcessingProducts.pending, (state) => {
        state.loadingExportProduct = true;
      })
      .addCase(exportProcessingProducts.fulfilled, (state) => {
        state.loadingExportProduct = false;
      })
      .addCase(exportProcessingProducts.rejected, (state) => {
        state.loadingExportProduct = false;
      })

      .addCase(exportInventoryProducts.pending, (state) => {
        state.loadingExportProduct = true;
      })
      .addCase(exportInventoryProducts.fulfilled, (state) => {
        state.loadingExportProduct = false;
      })
      .addCase(exportInventoryProducts.rejected, (state) => {
        state.loadingExportProduct = false;
      })

      // IMPORT WAREHOUSE CHECK
      .addCase(importWarehouseCheckGoogleSheets.pending, (state) => {
        state.loadingImportWarehouseCheck = true;
      })
      .addCase(importWarehouseCheckGoogleSheets.fulfilled, (state) => {
        state.loadingImportWarehouseCheck = false;
      })
      .addCase(importWarehouseCheckGoogleSheets.rejected, (state) => {
        state.loadingImportWarehouseCheck = false;
      })

      // IMPORT WAREHOUSE CHECK EXCEL
      .addCase(importWarehouseCheckExcel.pending, (state) => {
        state.loadingImportWarehouseCheckExcel = true;
      })
      .addCase(importWarehouseCheckExcel.fulfilled, (state) => {
        state.loadingImportWarehouseCheckExcel = false;
      })
      .addCase(importWarehouseCheckExcel.rejected, (state) => {
        state.loadingImportWarehouseCheckExcel = false;
      })

      // EXPORT WAREHOUSE CHECK
      .addCase(exportWarehouseCheckExcel.pending, (state) => {
        state.loadingExportWarehouseCheck = true;
      })
      .addCase(exportWarehouseCheckExcel.fulfilled, (state) => {
        state.loadingExportWarehouseCheck = false;
      })
      .addCase(exportWarehouseCheckExcel.rejected, (state) => {
        state.loadingExportWarehouseCheck = false;
      })

      // IMPORT EMPLOYEE
      .addCase(importEmployeesGoogleSheets.pending, (state) => {
        state.loadingImport = true;
      })
      .addCase(importEmployeesGoogleSheets.fulfilled, (state) => {
        state.loadingImport = false;
      })
      .addCase(importEmployeesGoogleSheets.rejected, (state) => {
        state.loadingImport = false;
      })

      // EXPORT EMPLOYEE
      .addCase(exportEmployeesExcel.pending, (state) => {
        state.loadingExport = true;
      })
      .addCase(exportEmployeesExcel.fulfilled, (state) => {
        state.loadingExport = false;
      })
      .addCase(exportEmployeesExcel.rejected, (state) => {
        state.loadingExport = false;
      })

      // EXPORT UNIT
      .addCase(exportUnitExcel.pending, (state) => {
        state.loadingExportUnit = true;
      })
      .addCase(exportUnitExcel.fulfilled, (state) => {
        state.loadingExportUnit = false;
      })
      .addCase(exportUnitExcel.rejected, (state) => {
        state.loadingExportUnit = false;
      })

      // EXPORT STOCK DETAIL
      .addCase(exportStockDetailExcel.pending, (state) => {
        state.loadingExportStockDetail = true;
      })
      .addCase(exportStockDetailExcel.fulfilled, (state) => {
        state.loadingExportStockDetail = false;
      })
      .addCase(exportStockDetailExcel.rejected, (state) => {
        state.loadingExportStockDetail = false;
      });
  },
});

export default materialSlice.reducer;